import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWabaAccounts, withAccount } from "../lib/waba";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiCopy,
  FiMic,
  FiMicOff,
  FiPhone,
  FiPhoneCall,
  FiPhoneForwarded,
  FiPhoneIncoming,
  FiPhoneOff,
  FiPlus,
  FiRadio,
  FiRefreshCw,
  FiShield,
  FiTrash2,
} from "react-icons/fi";
import { apiFetch } from "../lib/api";

const CONTACTS_KEY = "maa_whatsapp_calling_contacts";
const ICE_KEY = "maa_whatsapp_calling_ice_servers";
const DEFAULT_CONTACTS = [
  { id: "lead-1", name: "Priority Lead", phone: "919876543210" },
  { id: "lead-2", name: "Warm Prospect", phone: "918888777666" },
];
const DEFAULT_ICE = [
  { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
];
const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "DEL"];

const normalizePhone = (value) => String(value || "").replace(/\D/g, "");
const formatPhone = (value) => {
  const digits = normalizePhone(value);
  return digits ? `+${digits}` : "No number selected";
};
const formatDuration = (seconds) =>
  `${String(Math.floor((seconds || 0) / 60)).padStart(2, "0")}:${String(
    (seconds || 0) % 60
  ).padStart(2, "0")}`;
const statusMeta = (status) => {
  const value = String(status || "idle").toLowerCase();
  if (["accepted", "accept", "active", "connect"].some((item) => value.includes(item))) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (["failed", "reject", "terminate", "end"].some((item) => value.includes(item))) {
    return "border-red-200 bg-red-50 text-red-700";
  }
  if (["ring", "pending", "permission"].some((item) => value.includes(item))) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-100 text-slate-700";
};
const isTerminal = (status) =>
  ["failed", "completed", "rejected", "terminate", "ended", "missed"].some((item) =>
    String(status || "").toLowerCase().includes(item)
  );
const getDirection = (call) => call?.last_event?.direction || call?.direction || "UNKNOWN";
const normalizeDirection = (call) => {
  const value = String(getDirection(call) || "").trim().toLowerCase();
  if (["user_initiated", "user-initiated", "incoming", "inbound"].includes(value)) {
    return "INBOUND";
  }
  if (["business_initiated", "business-initiated", "outgoing", "outbound"].includes(value)) {
    return "OUTBOUND";
  }
  return String(getDirection(call) || "UNKNOWN").toUpperCase();
};
const getRemoteParty = (call, fallback = "") =>
  call?.counterparty ||
  call?.target ||
  call?.from_user ||
  call?.to_user ||
  call?.last_event?.from ||
  call?.last_event?.to ||
  fallback;
const asJson = (value) => JSON.stringify(value, null, 2);
const shortCallId = (value) => {
  const text = String(value || "");
  if (!text) return "--";
  if (text.length <= 36) return text;
  return `${text.slice(0, 18)}...${text.slice(-10)}`;
};
const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};
const getRecordingMeta = (call) => call?.provider_response?.recording || null;

const extractRemoteAnswer = (call) => {
  const candidates = [
    call?.session,
    call?.last_event?.session,
    call?.provider_response?.session,
    call?.provider_response?.call?.session,
  ];
  for (const item of candidates) {
    if (!item || typeof item !== "object") continue;
    if (item.answer?.sdp) {
      return { type: item.answer.type || "answer", sdp: item.answer.sdp };
    }
    if (item.sdp && (item.type || item.sdp_type || "answer") === "answer") {
      return { type: item.type || item.sdp_type || "answer", sdp: item.sdp };
    }
  }
  return null;
};

const extractIncomingOffer = (call) => {
  const candidates = [call?.session, call?.last_event?.session];
  for (const item of candidates) {
    if (!item?.sdp) continue;
    if ((item.type || item.sdp_type || "offer") === "offer") {
      return { type: item.type || item.sdp_type || "offer", sdp: item.sdp };
    }
  }
  return null;
};

// Whether a call is an answerable inbound ring. The backend computes this
// (status + direction + a usable offer + FRESHNESS) and stamps it on every
// session as `ringing`; the local inference is only a fallback for a backend
// build that predates the flag. The inference alone is what used to show a
// days-old ring as an incoming call forever — it has no clock, and an SDP
// offer is precisely the part of a call that does not age well.
const isIncomingPending = (call) =>
  call?.ringing === true ||
  (call?.ringing === undefined &&
    !isTerminal(call?.status) &&
    normalizeDirection(call) === "INBOUND" &&
    Boolean(extractIncomingOffer(call)));

// Meta names the action `action_name`; `action` / `name` cover older shapes.
const isStartCallAction = (item) =>
  (item?.action_name || item?.action || item?.name) === "start_call";

const describePermission = (response) => {
  const items = response?.data || response?.permissions || response?.actions;
  const permission = Array.isArray(items)
    ? items.find(isStartCallAction)
    : null;
  if (!permission) return "No permission data loaded yet.";
  return permission.can_perform_action
    ? "Recipient already has approved call permission."
    : "Recipient has not approved call permission yet.";
};

const getStartCallPermission = (response) => {
  const items = response?.data || response?.permissions || response?.actions;
  if (!Array.isArray(items)) return null;
  return (
    items.find(isStartCallAction) || null
  );
};

const formatSessionErrors = (errors) => {
  if (!Array.isArray(errors) || !errors.length) return "";
  return errors
    .map((item) => {
      const title = item?.title || item?.message || "WhatsApp error";
      const code = item?.code ? ` (${item.code})` : "";
      const details = item?.error_data?.details ? `: ${item.error_data.details}` : "";
      return `${title}${code}${details}`;
    })
    .filter(Boolean)
    .join(" | ");
};

export default function WhatsappCalling() {
  const [contacts, setContacts] = useState(DEFAULT_CONTACTS);
  const [mode, setMode] = useState("manual");
  const [manualNumber, setManualNumber] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(DEFAULT_CONTACTS[0]?.id || "");
  const [newContact, setNewContact] = useState({ name: "", phone: "" });
  const [permissionText, setPermissionText] = useState(
    "Allow this business to call you on WhatsApp."
  );
  const [permissionInfo, setPermissionInfo] = useState(null);
  const [permissionInfoNumber, setPermissionInfoNumber] = useState("");
  const [sessions, setSessions] = useState([]);
  const [activeCallId, setActiveCallId] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [backendMissing, setBackendMissing] = useState(false);
  const [callingAccount, setCallingAccount] = useState(null);
  // Calls are placed from, and listed for, the account chosen in the header.
  // Accept / reject / end name a call id and the backend resolves the account
  // from the call itself, so only the list, lookups and dial carry it.
  const { selected: wabaAccount } = useWabaAccounts();
  const [dialing, setDialing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [muted, setMuted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [iceText, setIceText] = useState(asJson(DEFAULT_ICE));
  const [iceError, setIceError] = useState("");
  const [serverIceServers, setServerIceServers] = useState([]);
  const [iceGatheringState, setIceGatheringState] = useState("new");
  const [iceConnectionState, setIceConnectionState] = useState("new");
  const [peerState, setPeerState] = useState("new");
  const [remoteReady, setRemoteReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState("");
  const [showCallLog, setShowCallLog] = useState(false);

  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const startedAtRef = useRef(null);
  const remoteAnswerAppliedRef = useRef(false);
  const flowRef = useRef("idle");
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingContextRef = useRef(null);
  const recordingDestinationRef = useRef(null);
  const localRecordingSourceRef = useRef(null);
  const remoteRecordingSourceRef = useRef(null);

  const selectedContact =
    contacts.find((item) => item.id === selectedContactId) || contacts[0] || null;
  const targetNumber = mode === "known" ? selectedContact?.phone || "" : manualNumber;
  const incomingCalls = useMemo(
    () => sessions.filter((session) => isIncomingPending(session)),
    [sessions]
  );
  const activeErrors = activeCall?.errors || activeCall?.last_event?.errors || [];
  const knownPermission = useMemo(() => {
    const normalizedTarget = normalizePhone(targetNumber);
    if (!normalizedTarget || permissionInfoNumber !== normalizedTarget) return null;
    return getStartCallPermission(permissionInfo?.provider_response);
  }, [permissionInfo, permissionInfoNumber, targetNumber]);

  useEffect(() => {
    try {
      const storedContacts = JSON.parse(window.localStorage.getItem(CONTACTS_KEY) || "null");
      if (Array.isArray(storedContacts) && storedContacts.length) {
        setContacts(storedContacts);
        setSelectedContactId(storedContacts[0]?.id || "");
      }
    } catch {}
    try {
      const storedIce = JSON.parse(window.localStorage.getItem(ICE_KEY) || "null");
      if (Array.isArray(storedIce) && storedIce.length) {
        setIceText(asJson(storedIce));
      }
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCapabilities = async () => {
      try {
        const data = await apiFetch(withAccount("/calling/capabilities", wabaAccount));
        if (cancelled) return;
        if (data?.account?.name) setCallingAccount(data.account);
        const iceServers = Array.isArray(data?.ice_servers) ? data.ice_servers : [];
        if (iceServers.length) {
          setServerIceServers(iceServers);
          setIceText(asJson(iceServers));
          setIceError("");
        }
      } catch {}
    };
    if (wabaAccount) loadCapabilities();
    return () => {
      cancelled = true;
    };
  }, [wabaAccount]);

  useEffect(() => {
    window.localStorage.setItem(CONTACTS_KEY, JSON.stringify(contacts));
  }, [contacts]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(iceText);
      if (!Array.isArray(parsed) || !parsed.length) {
        throw new Error("ICE server config must be a non-empty JSON array.");
      }
      window.localStorage.setItem(ICE_KEY, JSON.stringify(parsed));
      setIceError("");
    } catch (err) {
      setIceError(err.message || "ICE server JSON is invalid.");
    }
  }, [iceText]);

  useEffect(() => {
    let cancelled = false;
    const loadSessions = async () => {
      try {
        const data = await apiFetch(withAccount("/calling/sessions?limit=50", wabaAccount));
        if (!cancelled) setSessions(Array.isArray(data?.calls) ? data.calls : []);
      } catch (err) {
        if (cancelled) return;
        if (err.message === "Not Found") {
          setBackendMissing(true);
          setError("WhatsApp Calling APIs are not deployed on the backend yet.");
          return;
        }
        setError(err.message || "Unable to load calling sessions.");
      }
    };
    setSessions([]);
    loadSessions();
    const interval = window.setInterval(loadSessions, backendMissing ? 60000 : 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [backendMissing, wabaAccount]);

  useEffect(() => {
    if (!activeCallId) return;
    const next = sessions.find((item) => item.call_id === activeCallId);
    if (next) setActiveCall(next);
  }, [sessions, activeCallId]);

  useEffect(() => {
    if (!startedAtRef.current) return undefined;
    const interval = window.setInterval(() => {
      if (!startedAtRef.current) return; // frozen at the final duration
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [activeCallId]);

  useEffect(() => () => cleanupMedia(), []);

  useEffect(() => {
    if (!activeCallId || backendMissing) return undefined;
    let cancelled = false;
    const pollActive = async () => {
      try {
        const data = await apiFetch(`/calling/sessions/${activeCallId}`);
        if (!cancelled) {
          setActiveCall(data);
          applyRemoteAnswer(data);
          // A terminated call does not change again — refreshing it every
          // 2.5s for as long as the tab stays open only generates traffic.
          if (isTerminal(data?.status)) window.clearInterval(interval);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to refresh active call.");
      }
    };
    const interval = window.setInterval(pollActive, 2500);
    pollActive();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeCallId, backendMissing]);

  useEffect(() => {
    if (activeCall && isTerminal(activeCall.status)) cleanupMedia();
  }, [activeCall]);

  useEffect(() => {
    setRecordingUrl(getRecordingMeta(activeCall)?.url || "");
  }, [activeCall]);

  // Auth-aware transport. This page predates the platform requiring a token
  // and used bare fetch: it worked exactly as long as the browser happened to
  // hold a fresh session cookie, and surfaced expiry as whatever JSON the auth
  // layer answered with. apiFetch attaches the session, maps a 401 to a plain
  // sign-in message, and applies the standard timeout, same as every other
  // page. Paths are relative to /api.
  const fetchJson = (path, options = {}) => apiFetch(path, options);

  const resetPeerState = () => {
    setIceGatheringState("new");
    setIceConnectionState("new");
    setPeerState("new");
    setRemoteReady(false);
  };

  const resetRecordingGraph = () => {
    if (localRecordingSourceRef.current) {
      try {
        localRecordingSourceRef.current.disconnect();
      } catch {}
      localRecordingSourceRef.current = null;
    }
    if (remoteRecordingSourceRef.current) {
      try {
        remoteRecordingSourceRef.current.disconnect();
      } catch {}
      remoteRecordingSourceRef.current = null;
    }
    if (recordingContextRef.current) {
      try {
        recordingContextRef.current.close();
      } catch {}
      recordingContextRef.current = null;
    }
    recordingDestinationRef.current = null;
  };

  const attachRemoteStreamToRecorder = (remoteStream) => {
    if (!recordingContextRef.current || !recordingDestinationRef.current || !remoteStream) return;
    if (remoteRecordingSourceRef.current) {
      try {
        remoteRecordingSourceRef.current.disconnect();
      } catch {}
    }
    remoteRecordingSourceRef.current = recordingContextRef.current.createMediaStreamSource(remoteStream);
    remoteRecordingSourceRef.current.connect(recordingDestinationRef.current);
  };

  const stopCallRecording = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    await new Promise((resolve) => {
      recorder.addEventListener(
        "stop",
        async () => {
          const blob = new Blob(recordingChunksRef.current, {
            type: recorder.mimeType || "audio/webm",
          });
          mediaRecorderRef.current = null;
          recordingChunksRef.current = [];
          setRecording(false);
          resetRecordingGraph();

          if (!activeCallId || !blob.size) {
            resolve();
            return;
          }

          setUploadingRecording(true);
          try {
            const formData = new FormData();
            formData.append("call_id", activeCallId);
            formData.append("remote_party", normalizePhone(getRemoteParty(activeCall, targetNumber)));
            formData.append("file", new File([blob], `${activeCallId}.webm`, { type: blob.type }));
            const data = await fetchJson("/calling/recordings/upload", {
              method: "POST",
              body: formData,
            });
            setRecordingUrl(data?.url || "");
            setNote("Call recording uploaded successfully as MP3.");
          } catch (err) {
            setError(err.message || "Unable to upload call recording.");
          } finally {
            setUploadingRecording(false);
            resolve();
          }
        },
        { once: true }
      );
      recorder.stop();
    });
  };

  const startCallRecording = async () => {
    if (!streamRef.current) {
      setError("Start or accept a call before recording.");
      return;
    }
    if (!window.MediaRecorder) {
      setError("This browser does not support in-browser call recording.");
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      setError("This browser cannot build an audio recording graph.");
      return;
    }

    const context = new AudioContextClass();
    const destination = context.createMediaStreamDestination();
    localRecordingSourceRef.current = context.createMediaStreamSource(streamRef.current);
    localRecordingSourceRef.current.connect(destination);

    const remoteStream = remoteAudioRef.current?.srcObject;
    if (remoteStream) {
      remoteRecordingSourceRef.current = context.createMediaStreamSource(remoteStream);
      remoteRecordingSourceRef.current.connect(destination);
    }

    const preferredTypes = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "video/webm;codecs=vp8,opus",
    ];
    const mimeType =
      preferredTypes.find((item) => window.MediaRecorder.isTypeSupported?.(item)) || "";

    const recorder = mimeType
      ? new MediaRecorder(destination.stream, { mimeType })
      : new MediaRecorder(destination.stream);

    recordingContextRef.current = context;
    recordingDestinationRef.current = destination;
    recordingChunksRef.current = [];
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordingChunksRef.current.push(event.data);
      }
    };
    recorder.start(1000);
    setRecording(true);
    setRecordingUrl("");
    setNote("Recording started. It will upload as MP3 when stopped.");
  };

  const cleanupMedia = () => {
    stopCallRecording().catch(() => {});
    if (peerRef.current) {
      peerRef.current.ontrack = null;
      peerRef.current.oniceconnectionstatechange = null;
      peerRef.current.onconnectionstatechange = null;
      peerRef.current.onicegatheringstatechange = null;
      peerRef.current.close();
      peerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    remoteAnswerAppliedRef.current = false;
    flowRef.current = "idle";
    // Freeze the call timer at its final value. Every way a call ends comes
    // through here, and the ticking interval reads this ref.
    startedAtRef.current = null;
    setMuted(false);
    setRecording(false);
    resetPeerState();
  };

  const waitForIce = async (peer, timeoutMs = 8000) => {
    if (peer.iceGatheringState === "complete") return;
    await new Promise((resolve) => {
      const timeout = window.setTimeout(resolve, timeoutMs);
      const handler = () => {
        if (peer.iceGatheringState === "complete") {
          window.clearTimeout(timeout);
          peer.removeEventListener("icegatheringstatechange", handler);
          resolve();
        }
      };
      peer.addEventListener("icegatheringstatechange", handler);
    });
  };

  const buildPeerConnection = async ({ remoteOffer = null } = {}) => {
    cleanupMedia();
    const iceServers = serverIceServers.length ? serverIceServers : JSON.parse(iceText);
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    const peer = new RTCPeerConnection({ iceServers });
    stream.getTracks().forEach((track) => peer.addTrack(track, stream));
    peer.ontrack = (event) => {
      const [remoteStream] = event.streams;
      if (remoteAudioRef.current && remoteStream) remoteAudioRef.current.srcObject = remoteStream;
      attachRemoteStreamToRecorder(remoteStream);
      setRemoteReady(true);
    };
    peer.onicegatheringstatechange = () => setIceGatheringState(peer.iceGatheringState);
    peer.oniceconnectionstatechange = () => {
      setIceConnectionState(peer.iceConnectionState);
      if (["failed", "disconnected"].includes(peer.iceConnectionState)) {
        setError(
          serverIceServers.length
            ? "WebRTC media path is unhealthy. Check the configured TURN relay and network reachability."
            : "WebRTC media path is unhealthy. Configure TURN relay servers on the backend to avoid intermittent 138021 failures."
        );
      }
    };
    peer.onconnectionstatechange = () => setPeerState(peer.connectionState);
    streamRef.current = stream;
    peerRef.current = peer;
    if (remoteOffer) {
      await peer.setRemoteDescription(new RTCSessionDescription(remoteOffer));
      await peer.setLocalDescription(await peer.createAnswer());
    } else {
      await peer.setLocalDescription(await peer.createOffer({ offerToReceiveAudio: true }));
    }
    await waitForIce(peer);
    return { type: peer.localDescription.type, sdp: peer.localDescription.sdp };
  };

  const applyRemoteAnswer = (call) => {
    if (!peerRef.current || flowRef.current !== "outbound" || remoteAnswerAppliedRef.current) {
      return;
    }
    const answer = extractRemoteAnswer(call);
    if (!answer?.sdp) return;
    peerRef.current
      .setRemoteDescription(new RTCSessionDescription(answer))
      .then(() => {
        remoteAnswerAppliedRef.current = true;
      })
      .catch((err) => setError(err.message || "Unable to apply remote SDP answer."));
  };

  const beginTracking = (callId, call, flow) => {
    flowRef.current = flow;
    startedAtRef.current = Date.now();
    setSeconds(0);
    setActiveCallId(callId || "");
    setActiveCall(call || null);
    setRecordingUrl(call?.provider_response?.recording?.url || "");
  };

  const clearActiveCallView = () => {
    setActiveCallId("");
    setActiveCall(null);
    setRecordingUrl("");
    startedAtRef.current = null;
    setSeconds(0);
  };

  const checkPermission = async () => {
    const number = normalizePhone(targetNumber);
    if (!number) return setError("Enter or select a number first.");
    setError("");
    const data = await fetchJson(withAccount(`/calling/permissions/${number}`, wabaAccount));
    setPermissionInfo(data);
    setPermissionInfoNumber(number);
    setNote("Permission state refreshed.");
  };

  const requestPermission = async () => {
    const number = normalizePhone(targetNumber);
    if (!number) return setError("Enter or select a number first.");
    setError("");
    const data = await fetchJson("/calling/permission-template", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone_number: mode === "manual" ? number : undefined,
        known_number: mode === "known" ? number : undefined,
        contact_name: selectedContact?.name || "",
        waba_account: wabaAccount || undefined,
        body_text: permissionText,
        template_name: "user_onboarding",
        template_category: "dispute_resolution",
        language_code: "en_US",
      }),
    });
    setNote(
      data?.message_id
        ? "Onboarding template sent. When the user taps the quick reply, the call permission request will be sent automatically."
        : "Onboarding template was submitted, but no message id was returned."
    );
  };

  const startCall = async () => {
    const number = normalizePhone(targetNumber);
    if (!number) return setError("Enter or select a number first.");
    if (knownPermission?.can_perform_action === false) {
      setError("This number does not currently have approved WhatsApp call permission.");
      return;
    }
    clearActiveCallView();
    setDialing(true);
    setError("");
    setNote("");
    try {
      const session = await buildPeerConnection();
      const data = await fetchJson("/calling/dial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: mode === "manual" ? number : undefined,
          known_number: mode === "known" ? number : undefined,
          contact_name: selectedContact?.name || "",
          waba_account: wabaAccount || undefined,
          session,
        }),
      });
      beginTracking(data.call_id, data, "outbound");
      if (data?.permission_state) {
        setPermissionInfo({ user_wa_id: number, provider_response: data.permission_state });
        setPermissionInfoNumber(number);
      }
      setNote("Outbound call request sent to WhatsApp.");
    } catch (err) {
      cleanupMedia();
      const message = err.message || "Unable to start the call.";
      if (message.includes("permission is not granted") || message.includes("(138006)")) {
        setPermissionInfoNumber(number);
      }
      setError(message);
    } finally {
      setDialing(false);
    }
  };

  const acceptIncoming = async (call) => {
    const offer = extractIncomingOffer(call);
    if (!offer?.sdp) return setError("Incoming call does not include a usable SDP offer.");
    setAccepting(true);
    setError("");
    setNote("");
    try {
      const session = await buildPeerConnection({ remoteOffer: offer });
      const data = await fetchJson("/calling/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call_id: call.call_id, session }),
      });
      beginTracking(call.call_id, { ...call, provider_response: data.provider_response }, "inbound");
      setNote("Incoming call accepted. Waiting for media to flow.");
    } catch (err) {
      cleanupMedia();
      setError(err.message || "Unable to accept the call.");
    } finally {
      setAccepting(false);
    }
  };

  const rejectIncoming = async (callId) => {
    setRejecting(true);
    setError("");
    try {
      await fetchJson("/calling/reject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call_id: callId }),
      });
      setNote("Incoming call rejected.");
    } catch (err) {
      setError(err.message || "Unable to reject the call.");
    } finally {
      setRejecting(false);
    }
  };

  const activeCallOver = Boolean(activeCall) && isTerminal(activeCall.status);

  const endCall = async () => {
    if (!activeCallId) return;
    if (activeCallOver) {
      // The call is already over — the button reads "Close" in this state and
      // just clears the cockpit. Sending terminate again would overwrite the
      // real final status and ask Meta to end a call it already ended.
      cleanupMedia();
      clearActiveCallView();
      setNote("");
      return;
    }
    setEnding(true);
    setError("");
    try {
      await fetchJson("/calling/end", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call_id: activeCallId }),
      });
      setNote("Call end requested.");
    } catch (err) {
      setError(err.message || "Unable to end the call.");
    } finally {
      cleanupMedia();
      setEnding(false);
    }
  };

  const toggleMute = () => {
    if (!streamRef.current) return;
    const next = !muted;
    streamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    setMuted(next);
  };

  const addContact = () => {
    const name = newContact.name.trim();
    const phone = normalizePhone(newContact.phone);
    if (!name || phone.length < 8) return setError("Add a name and a valid phone number.");
    const item = { id: `contact-${Date.now()}`, name, phone };
    setContacts((current) => [item, ...current]);
    setMode("known");
    setSelectedContactId(item.id);
    setNewContact({ name: "", phone: "" });
    setError("");
  };

  const removeContact = (id) => {
    const next = contacts.filter((item) => item.id !== id);
    setContacts(next);
    if (selectedContactId === id) setSelectedContactId(next[0]?.id || "");
  };

  const copyCallId = () => {
    if (!activeCallId) return;
    navigator.clipboard.writeText(activeCallId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <div className="mx-auto w-full max-w-[1560px] px-4 py-4 sm:px-6 sm:py-8">
        <div className="rounded-[32px] bg-[linear-gradient(135deg,#000000,#D1272F_58%,#F96168)] p-8 text-white shadow-2xl">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">
                <FiPhoneCall className="h-3.5 w-3.5" />
                Whatsapp Calling
                {callingAccount ? (
                  <span className="rounded-full bg-white/15 px-2 py-0.5 normal-case tracking-normal">
                    via {callingAccount.label || callingAccount.name}
                  </span>
                ) : null}
              </div>
              <h1 className="mt-4 text-3xl font-semibold">
                End-to-end WhatsApp voice calling desk
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-brand-50/85">
                Request permission, answer inbound calls, dial outbound calls, and inspect the
                signaling and media path from one operator screen.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <div className="rounded-2xl bg-white/10 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-white/75">Sessions</div>
                <div className="mt-2 text-2xl font-semibold">{sessions.length}</div>
              </div>
              <div className="rounded-2xl bg-white/10 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-white/75">Incoming</div>
                <div className="mt-2 text-2xl font-semibold">{incomingCalls.length}</div>
              </div>
              <div className="rounded-2xl bg-white/10 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-white/75">Timer</div>
                <div className="mt-2 text-2xl font-semibold">{formatDuration(seconds)}</div>
              </div>
              <div className="rounded-2xl bg-white/10 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-white/75">Remote Audio</div>
                <div className="mt-2 text-2xl font-semibold">{remoteReady ? "LIVE" : "WAIT"}</div>
              </div>
            </div>
          </div>
        </div>
        {error ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="mt-0.5 h-5 w-5 flex-none" />
              <div>{error}</div>
            </div>
          </div>
        ) : null}
        {note ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-700">
            <div className="flex items-start gap-3">
              <FiCheckCircle className="mt-0.5 h-5 w-5 flex-none" />
              <div>{note}</div>
            </div>
          </div>
        ) : null}
        {incomingCalls[0] ? (
          <section className="mt-6 rounded-3xl border border-amber-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
                  <FiPhoneIncoming className="h-3.5 w-3.5" />
                  Incoming WhatsApp Call
                </div>
                <div className="mt-3 text-2xl font-semibold text-slate-900">
                  {formatPhone(getRemoteParty(incomingCalls[0]))}
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => acceptIncoming(incomingCalls[0])}
                  disabled={accepting || rejecting || ending || backendMissing || Boolean(iceError)}
                  className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {accepting ? "Accepting..." : "Accept Call"}
                </button>
                <button
                  onClick={() => rejectIncoming(incomingCalls[0].call_id)}
                  disabled={rejecting || accepting || backendMissing}
                  className="rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm font-semibold text-red-700 disabled:opacity-60"
                >
                  {rejecting ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        <div className="mt-6 grid gap-6 xl:grid-cols-[380px,minmax(0,1fr)] 2xl:grid-cols-[420px,minmax(0,1fr)]">
          <section className="h-fit rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <FiPhone className="h-5 w-5 text-brand-700" />
              <h2 className="text-lg font-semibold text-slate-900">Dial Workspace</h2>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode("manual")}
                className={`rounded-2xl border px-4 py-3 text-left ${
                  mode === "manual"
                    ? "border-brand-400 bg-brand-50 text-brand-700"
                    : "border-slate-200 text-slate-600"
                }`}
              >
                <div className="font-semibold">Manual Number</div>
                <div className="mt-1 text-xs">Dial any WhatsApp number</div>
              </button>
              <button
                onClick={() => setMode("known")}
                className={`rounded-2xl border px-4 py-3 text-left ${
                  mode === "known"
                    ? "border-brand-400 bg-brand-50 text-brand-700"
                    : "border-slate-200 text-slate-600"
                }`}
              >
                <div className="font-semibold">Known Lead</div>
                <div className="mt-1 text-xs">Use a saved number</div>
              </button>
            </div>
            <div className="mt-5">
              {mode === "manual" ? (
                <input
                  value={manualNumber}
                  onChange={(event) => setManualNumber(normalizePhone(event.target.value))}
                  placeholder="Enter country code and phone number"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-4 text-lg font-semibold outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
                />
              ) : (
                <div className="space-y-3">
                  {contacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => setSelectedContactId(contact.id)}
                      className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left ${
                        selectedContactId === contact.id
                          ? "border-brand-400 bg-brand-50"
                          : "border-slate-200"
                      }`}
                    >
                      <div>
                        <div className="font-semibold text-slate-900">{contact.name}</div>
                        <div className="mt-1 text-sm text-slate-500">{formatPhone(contact.phone)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Current Target
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">
                {formatPhone(targetNumber)}
              </div>
            </div>
            <textarea
              rows={4}
              value={permissionText}
              onChange={(event) => setPermissionText(event.target.value)}
              className="mt-5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            />
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <button
                onClick={checkPermission}
                disabled={backendMissing}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  <FiShield className="h-4 w-4" />
                  Check
                </span>
              </button>
              <button
                onClick={requestPermission}
                disabled={backendMissing}
                className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm font-semibold text-brand-700 disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  <FiPhoneForwarded className="h-4 w-4" />
                  Request
                </span>
              </button>
              <button
                onClick={startCall}
                disabled={
                  dialing ||
                  accepting ||
                  ending ||
                  backendMissing ||
                  Boolean(iceError) ||
                  knownPermission?.can_perform_action === false
                }
                className="rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  <FiPhoneCall className="h-4 w-4" />
                  {dialing ? "Dialing..." : "Start Call"}
                </span>
              </button>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {DIGITS.map((digit) => (
                <button
                  key={digit}
                  onClick={() => {
                    if (mode !== "manual") return;
                    if (digit === "DEL") {
                      setManualNumber((current) => current.slice(0, -1));
                      return;
                    }
                    if (digit !== "+") setManualNumber((current) => `${current}${digit}`);
                  }}
                  className={`aspect-square min-h-[72px] rounded-2xl border px-4 py-4 text-lg font-semibold ${
                    mode === "manual"
                      ? "border-slate-200 bg-slate-50 text-slate-800"
                      : "border-slate-100 bg-slate-50 text-slate-300"
                  }`}
                >
                  {digit === "DEL" ? "Del" : digit}
                </button>
              ))}
            </div>
            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">Permission State</div>
              <div className="mt-2 text-sm text-slate-600">
                {describePermission(permissionInfo?.provider_response)}
              </div>
              <div className="mt-3 text-xs leading-6 text-slate-500">
                Permission request messages can still fail if more than 24 hours have passed since
                the customer last replied to your business number.
              </div>
            </div>
          </section>

          <section className="min-w-0 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <FiPhoneCall className="h-5 w-5 text-emerald-700" />
                <h2 className="text-lg font-semibold text-slate-900">Live Call Cockpit</h2>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600"
              >
                <FiRefreshCw className="h-4 w-4" />
                Reload
              </button>
            </div>
            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5">
              {/* Wraps rather than sitting side by side at a fixed breakpoint: the
                  buttons used to carry a 480px min-width next to the number, which
                  pushed End past the card's edge whenever the card was narrower
                  than the two together. */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    Active Session
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-slate-900">
                    {formatPhone(getRemoteParty(activeCall, targetNumber))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <div
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusMeta(
                        activeCall?.status
                      )}`}
                    >
                      {String(activeCall?.status || "idle").toUpperCase()}
                    </div>
                    <div className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                      {normalizeDirection(activeCall)}
                    </div>
                  </div>
                </div>
                <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 2xl:w-auto">
                  <button
                    onClick={toggleMute}
                    disabled={!streamRef.current || backendMissing}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60"
                  >
                    <span className="inline-flex items-center gap-2">
                      {muted ? <FiMicOff className="h-4 w-4" /> : <FiMic className="h-4 w-4" />}
                      {muted ? "Unmute" : "Mute"}
                    </span>
                  </button>
                  <button
                    onClick={copyCallId}
                    disabled={!activeCallId}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FiCopy className="h-4 w-4" />
                      {copied ? "Copied" : "Copy ID"}
                    </span>
                  </button>
                  <button
                    onClick={recording ? stopCallRecording : startCallRecording}
                    disabled={!activeCallId || backendMissing || uploadingRecording}
                    className={`rounded-2xl px-4 py-3 text-sm font-semibold disabled:opacity-60 ${
                      recording
                        ? "bg-amber-500 text-white"
                        : "border border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FiRadio className="h-4 w-4" />
                      {uploadingRecording
                        ? "Uploading..."
                        : recording
                        ? "Stop Rec"
                        : "Record"}
                    </span>
                  </button>
                  <button
                    onClick={endCall}
                    disabled={!activeCallId || ending || backendMissing}
                    className={`rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60 ${
                      activeCallOver ? "bg-slate-600" : "bg-red-600"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FiPhoneOff className="h-4 w-4" />
                      {ending ? "Ending..." : activeCallOver ? "Close" : "End"}
                    </span>
                  </button>
                </div>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">ICE Gathering</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{iceGatheringState}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">ICE Connection</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{iceConnectionState}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Peer State</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{peerState}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Call ID</div>
                  <div className="mt-2 break-all text-sm font-semibold text-slate-900">{shortCallId(activeCallId)}</div>
                </div>
              </div>
              {activeCall?.permission_reply ? (
                <div className="mt-4 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700">
                  Permission reply:{" "}
                  <span className="font-semibold">
                    {String(activeCall.permission_reply.response || "unknown").toUpperCase()}
                  </span>
                </div>
              ) : null}
              {activeErrors.length ? (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {activeErrors.map((item, index) => (
                    <div key={`${item.code || "err"}-${index}`}>
                      {item.title || item.message || "Call error"}
                      {item.code ? ` (${item.code})` : ""}
                      {item.error_data?.details ? `: ${item.error_data.details}` : ""}
                    </div>
                  ))}
                </div>
              ) : null}
              {recordingUrl ? (
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  Recording ready:{" "}
                  <a
                    href={recordingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold underline"
                    download
                  >
                    Download MP3
                  </a>
                </div>
              ) : null}
            </div>
            <div className="mt-6 grid gap-6 2xl:grid-cols-[minmax(0,1.15fr),minmax(320px,0.85fr)]">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-900">Recent Sessions</div>
                  <button
                    onClick={() => setShowCallLog((current) => !current)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                  >
                    {showCallLog ? "Hide Call Log" : "Show Call Log"}
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  {sessions.length ? (
                    sessions.map((session) => (
                      <div
                        key={session.call_id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {formatPhone(getRemoteParty(session))}
                            </div>
                            <div className="mt-1 break-all text-xs text-slate-500">{shortCallId(session.call_id)}</div>
                            {formatSessionErrors(session.errors) ? (
                              <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                {formatSessionErrors(session.errors)}
                              </div>
                            ) : null}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusMeta(
                                  session.status
                                )}`}
                              >
                                {String(session.status || "idle").toUpperCase()}
                              </span>
                              <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                {normalizeDirection(session)}
                              </span>
                              {getRecordingMeta(session)?.url ? (
                                <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                  MP3 REC
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {isIncomingPending(session) ? (
                              <button
                                onClick={() => acceptIncoming(session)}
                                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
                              >
                                Accept
                              </button>
                            ) : null}
                            <button
                              onClick={() => {
                                setMode("manual");
                                setManualNumber(normalizePhone(getRemoteParty(session, "")));
                              }}
                              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                            >
                              Redial
                            </button>
                            <button
                              onClick={() => {
                                setActiveCallId(session.call_id);
                                setActiveCall(session);
                              }}
                              className="rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-700"
                            >
                              Open
                            </button>
                            {getRecordingMeta(session)?.url ? (
                              <a
                                href={getRecordingMeta(session).url}
                                download
                                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
                              >
                                Download MP3
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No call sessions yet.
                    </div>
                  )}
                </div>
                {showCallLog ? (
                  <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">
                      Call Log Table
                    </div>
                    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                      <table className="min-w-[720px] w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-slate-600">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Number</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold">Direction</th>
                            <th className="px-4 py-3 font-semibold">Updated</th>
                            <th className="px-4 py-3 font-semibold">Call ID</th>
                            <th className="px-4 py-3 font-semibold">Recording</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sessions.map((session) => {
                            const recording = getRecordingMeta(session);
                            return (
                              <tr key={`table-${session.call_id}`} className="align-top">
                                <td className="px-4 py-3 font-medium text-slate-900">
                                  {formatPhone(getRemoteParty(session))}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {String(session.status || "idle").toUpperCase()}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {normalizeDirection(session)}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatDateTime(session.updated_at)}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">
                                  {shortCallId(session.call_id)}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {recording?.url ? (
                                    <div className="space-y-1">
                                      <a
                                        href={recording.url}
                                        download
                                        className="inline-flex rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 font-semibold text-emerald-700"
                                      >
                                        Download MP3
                                      </a>
                                      <div className="text-xs text-slate-500">
                                        {recording.filename || "recording.mp3"}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">Not recorded</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Known Numbers</div>
                <div className="mt-3 space-y-3">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <div>
                        <div className="font-semibold text-slate-900">{contact.name}</div>
                        <div className="mt-1 text-sm text-slate-500">{formatPhone(contact.phone)}</div>
                      </div>
                      <button
                        onClick={() => removeContact(contact.id)}
                        className="inline-flex min-h-[40px] items-center justify-center rounded-xl border border-red-200 px-3 py-2 text-red-600"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
                    <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <FiPlus className="h-4 w-4 text-brand-700" />
                      Add Known Number
                    </div>
                    <div className="space-y-3">
                      <input
                        value={newContact.name}
                        onChange={(event) =>
                          setNewContact((current) => ({ ...current, name: event.target.value }))
                        }
                        placeholder="Contact name"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
                      />
                      <input
                        value={newContact.phone}
                        onChange={(event) =>
                          setNewContact((current) => ({
                            ...current,
                            phone: normalizePhone(event.target.value),
                          }))
                        }
                        placeholder="Country code + number"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
                      />
                      <button
                        onClick={addContact}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
                      >
                        <FiPlus className="h-4 w-4" />
                        Save Known Number
                      </button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-semibold text-slate-900">Connection Setup</div>
                    <div className="mt-2 text-sm leading-6 text-slate-600">
                      Audio routing is active with the saved browser ICE configuration. If network-specific audio issues ever return, we can add TURN configuration again without changing the backend.
                    </div>
                    {iceError ? (
                      <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {iceError}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
